import { useId } from 'react'

const skull = new URL('skull.svg', import.meta.url).href
const ape = new URL('ape.png', import.meta.url).href
const droid = new URL('droid.png', import.meta.url).href
const lootbox = new URL('lootbox.png', import.meta.url).href
const weapon = new URL('weapon.png', import.meta.url).href

/**
 * Every instance used to stamp the same `id`s (`mask`, `polygon`, `svg_br1gradient`, …) into the
 * document. Duplicate ids are not an error — `url(#mask)` simply resolves to whichever element
 * came first in DOM order — so an icon rendered at one size silently borrowed the mask geometry of
 * an unrelated icon elsewhere on the page, and rendered cropped or unfilled.
 *
 * Ids are now scoped per instance. The public contract is unchanged: callers still pass
 * `fill='url(#svg_legendarygradient)'` and `scopeFill` rewrites it to this instance's id.
 */
const useScopedIds = () => {
    // useId() emits colons, which are legal in an id but awkward inside url(#…) references.
    const uid = useId().replace(/:/g, '')
    const scope = (name: string) => `${name}-${uid}`
    // Note the digit class: `svg_br1gradient` has a 1 in it.
    const scopeFill = (value?: string) =>
        value?.replace(/url\(#(svg_[a-z0-9_]+)\)/gi, (_match, name) => `url(#${scope(name)})`)
    return { scope, scopeFill }
}

const Hexagon = ({ children, fill, stroke, className }: HexagonProps & IconProps) => {
    const { scope, scopeFill } = useScopedIds()
    return (
        <svg viewBox='0 0 62 52' fill={scopeFill(fill)} stroke={stroke} className={className}>
            <defs>
                <linearGradient
                    id={scope('svg_br1gradient')}
                    x1='12.5509'
                    y1='-3.05291'
                    x2='49.525'
                    y2='53.5955'
                    gradientUnits='userSpaceOnUse'
                >
                    <stop stopColor='#03CEFF' />
                    <stop offset='1' stopColor='#C91FFF' />
                </linearGradient>
                <linearGradient id={scope('svg_legendarygradient')} x2='150%' y2='150%' gradientUnits='userSpaceOnUse'>
                    <stop stopColor='#FFE500' />
                    <stop offset='1' stopOpacity='0' />
                </linearGradient>
                <linearGradient
                    id={scope('svg_weapon_legendary_gradient')}
                    x2='150%'
                    y2='150%'
                    gradientUnits='userSpaceOnUse'
                >
                    <stop stopColor='#C91FFF' />
                    <stop offset='1' stopOpacity='0' />
                </linearGradient>
                <linearGradient id={scope('svg_commongradient')} x2='150%' y2='150%' gradientUnits='userSpaceOnUse'>
                    <stop stopColor='#B9B9B9' />
                    <stop offset='1' stopOpacity='0' />
                </linearGradient>
            </defs>
            <symbol viewBox='0 0 72 72' id={scope('polygon')}>
                <path
                    fill='white'
                    d='M7.577,37 C7.22,36.381 7.22,35.619 7.577,35 L20.923,11.885 C21.28,11.266 21.94,10.885 22.655,10.885 L49.345,10.885 C50.06,10.885 50.72,11.266 51.077,11.885 L64.423,35 C64.78,35.619 64.78,36.381 64.423,37 L51.077,60.115 C50.72,60.734 50.06,61.115 49.345,61.115 L22.655,61.115 C21.94,61.115 21.28,60.734 20.923,60.115 L7.577,37 z'
                />
            </symbol>
            <mask id={scope('mask')}>
                <use href={`#${scope('polygon')}`} x='5%' y='5%' width={'90%'} height={'90%'} />
            </mask>
            <path
                d='M2.13622 25.4961L1.8423 26L2.13622 26.5039L16.1362 50.5039L16.4256 51H17H45H45.5744L45.8638 50.5039L59.8638 26.5039L60.1577 26L59.8638 25.4961L45.8638 1.49613L45.5744 1H45H17H16.4256L16.1362 1.49613L2.13622 25.4961Z'
                strokeWidth='2'
                fill='none'
            />
            <path
                d='M41.495 8.0147L51.7877 26.1839L41.4226 44.1009H20.4587L10.2082 26.196L20.5099 8.0147H41.4935M44.13 3.48486H17.8749L5 26.208L17.8372 48.6307H44.0305L57.002 26.208L44.13 3.48486Z'
                strokeWidth='0'
            />
            {typeof children === 'function' ? children(`url(#${scope('mask')})`) : children}
        </svg>
    )
}

/** An icon whose artwork is clipped to the hexagon. `inset` is the margin left around it. */
const maskedIcon =
    (href: string, defaultFill: string, size: number) =>
    (props: IconProps) => {
        const offset = (100 - size) / 2
        return (
            <Hexagon fill={props.fill ?? defaultFill} stroke={props.stroke} className={props.className}>
                {(mask) => (
                    <image
                        href={href}
                        mask={mask}
                        width={`${size}%`}
                        height={`${size}%`}
                        x={`${offset}%`}
                        y={`${offset}%`}
                    />
                )}
            </Hexagon>
        )
    }

const SkullPoint = maskedIcon(skull, 'url(#svg_br1gradient)', 80)
const Ape = maskedIcon(ape, '#C91FFF', 60)
const Droid = maskedIcon(droid, '#0088D5', 60)
const Lootbox = maskedIcon(lootbox, '#00FF90', 30)
const Weapon = maskedIcon(weapon, '#FFE500', 40)

const UnknownReward = (props: IconProps) => {
    return (
        <Hexagon fill={props.fill ?? '#FF2A43'} stroke={props.stroke} className={props.className}>
            <foreignObject
                width={'40%'}
                height={'40%'}
                x={'30%'}
                y={'25%'}
                className='text-center font-bold text-white'
            >
                ?
            </foreignObject>
        </Hexagon>
    )
}

export const Icons = {
    Ape,
    Droid,
    Lootbox,
    SkullPoint,
    Weapon,
    UnknownReward,
}
